import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { useBranding } from "@/lib/branding";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { GraduationCap, ShieldCheck } from "lucide-react";
import PoweredBy from "@/components/PoweredBy";
import GoogleLoginButton from "@/components/GoogleLoginButton";
import { toast } from "sonner";

function SignInForm({ role, testPrefix }) {
  const { login, logout } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await login(email, password);
      if (role === "admin" && u.role !== "admin") {
        // Student credentials submitted on Instructor tab — reject
        logout();
        toast.error("This account is not an administrator. Use the Student tab.");
        setLoading(false);
        return;
      }
      toast.success(`Welcome back, ${u.name}`);
      nav(u.role === "admin" ? "/admin/dashboard" : "/dashboard");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5" data-testid={`${testPrefix}-form`}>
      <div className="space-y-2">
        <Label htmlFor={`${testPrefix}-email`}>Email</Label>
        <Input id={`${testPrefix}-email`} data-testid={`${testPrefix}-email`} type="email" value={email} onChange={e => setEmail(e.target.value)} required autoComplete="email" />
      </div>
      <div className="space-y-2">
        <Label htmlFor={`${testPrefix}-password`}>Password</Label>
        <Input id={`${testPrefix}-password`} data-testid={`${testPrefix}-password`} type="password" value={password} onChange={e => setPassword(e.target.value)} required autoComplete="current-password" />
      </div>
      <Button type="submit" data-testid={`${testPrefix}-submit`} disabled={loading} className="w-full">
        {loading ? "Signing in…" : role === "admin" ? "Sign in as instructor" : "Sign in as student"}
      </Button>
    </form>
  );
}

export default function Login() {
  const { branding } = useBranding();

  return (
    <div className="min-h-screen grid md:grid-cols-5 bg-background paper-texture">
      <div className="md:col-span-3 lg:col-span-3 relative hidden md:block bg-primary">
        <img
          src="https://customer-assets-jai6qajn.emergentagent.net/job_tax-101-learn/artifacts/qv8p8w2v_7B2A2B02-2927-408E-8E22-02A848BF3570.png"
          alt="DyNamic Tax white-label system preview"
          className="absolute inset-0 h-full w-full object-contain"
          data-testid="login-hero-image"
        />
      </div>

      <div className="md:col-span-2 flex items-center justify-center p-6 md:p-8">
        <div className="w-full max-w-sm">
          <div className="mb-8">
            <div className="label-caps mb-2">Sign in</div>
            <h2 className="font-serif text-4xl">Welcome back.</h2>
            <p className="text-sm text-muted-foreground mt-2">Choose your role to sign in.</p>
          </div>

          <Tabs defaultValue="student" className="w-full">
            <TabsList className="grid grid-cols-2 mb-6 h-auto">
              <TabsTrigger value="student" data-testid="tab-student" className="gap-2 py-2">
                <GraduationCap size={14} /> Student
              </TabsTrigger>
              <TabsTrigger value="instructor" data-testid="tab-instructor" className="gap-2 py-2">
                <ShieldCheck size={14} /> Instructor
              </TabsTrigger>
            </TabsList>

            <TabsContent value="student">
              <div className="space-y-4">
                <GoogleLoginButton label="Sign in with Google" testId="google-login-student" />
                <div className="relative">
                  <div className="absolute inset-0 flex items-center"><span className="w-full border-t border-border" /></div>
                  <div className="relative flex justify-center text-[10px] uppercase tracking-widest">
                    <span className="bg-background px-3 text-muted-foreground">or use email</span>
                  </div>
                </div>
                <SignInForm role="student" testPrefix="login" />
              </div>
              <div className="mt-4 text-right">
                <Link data-testid="forgot-password-link" to="/forgot-password" className="text-xs underline text-muted-foreground">Forgot password?</Link>
              </div>
              <p className="text-sm text-muted-foreground mt-4">
                New student? <Link data-testid="go-register" to="/register" className="underline">Create an account</Link>
              </p>
            </TabsContent>

            <TabsContent value="instructor">
              <div className="space-y-4">
                <GoogleLoginButton label="Sign in with Google" testId="google-login-instructor" />
                <div className="relative">
                  <div className="absolute inset-0 flex items-center"><span className="w-full border-t border-border" /></div>
                  <div className="relative flex justify-center text-[10px] uppercase tracking-widest">
                    <span className="bg-background px-3 text-muted-foreground">or use email</span>
                  </div>
                </div>
                <SignInForm role="admin" testPrefix="admin-login" />
              </div>
              <div className="mt-4 text-right">
                <Link data-testid="forgot-password-link-instructor" to="/forgot-password" className="text-xs underline text-muted-foreground">Forgot password?</Link>
              </div>
              <p className="text-xs text-muted-foreground mt-4">
                Instructor accounts are unlocked when you buy the <Link to="/pricing" className="underline" data-testid="instructor-pricing-link">Instructor plan</Link>, or when the school adds you as an instructor. If you're a student, use the Student tab.
              </p>
            </TabsContent>
          </Tabs>

          <PoweredBy className="md:hidden mt-8 text-center" />
        </div>
      </div>
    </div>
  );
}
